// ============================================================================
// FIRESTORE SIGNALING - Use Firebase Firestore for WebRTC signaling
// ============================================================================
// No custom server needed! Firestore handles real-time message passing.

import { EventEmitter } from './EventEmitter';

export interface FirestoreSignalingEvents {
  connected: void;
  disconnected: void;
  roomCreated: { roomCode: string };
  roomJoined: { roomCode: string; peers: string[] };
  peerJoined: { peerId: string };
  peerLeft: { peerId: string };
  offer: { peerId: string; offer: RTCSessionDescriptionInit };
  answer: { peerId: string; answer: RTCSessionDescriptionInit };
  iceCandidate: { peerId: string; candidate: RTCIceCandidateInit };
  error: { message: string };
}

export class FirestoreSignaling extends EventEmitter<FirestoreSignalingEvents> {
  private db: any = null; // Firestore instance
  private localPeerId: string;
  private roomCode: string = '';
  private unsubscribers: (() => void)[] = [];
  private isConnected = false;

  // Firebase modules (loaded dynamically)
  private firebase: any = null;

  constructor() {
    super();
    this.localPeerId = this.generatePeerId();
  }

  private generatePeerId(): string {
    return `peer_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async connect(firebaseConfig: Record<string, string>): Promise<void> {
    try {
      // Dynamically import Firebase
      const { initializeApp, getApps, getApp } = await import('firebase/app');
      const { getFirestore, collection, doc, setDoc, getDoc, addDoc, onSnapshot, deleteDoc, updateDoc, serverTimestamp, arrayUnion } = await import('firebase/firestore');

      // Store firebase functions for later use
      this.firebase = { 
        collection, doc, setDoc, getDoc, addDoc, onSnapshot, 
        deleteDoc, updateDoc, serverTimestamp, arrayUnion
      };

      // Initialize Firebase (or use existing app if already initialized)
      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
      this.db = getFirestore(app);

      this.isConnected = true;
      this.emit('connected', undefined);
      console.log('[FirestoreSignaling] Connected to Firebase');
    } catch (e) {
      console.error('[FirestoreSignaling] Failed to connect:', e);
      throw e;
    }
  }

  async createRoom(): Promise<string> {
    if (!this.db) throw new Error('Not connected to Firebase');

    const { doc, setDoc, serverTimestamp } = this.firebase;

    // Generate room code
    this.roomCode = this.generateRoomCode();

    // Create room document
    const roomRef = doc(this.db, 'rooms', this.roomCode);
    await setDoc(roomRef, {
      hostId: this.localPeerId,
      createdAt: serverTimestamp(),
      peers: [this.localPeerId],
    });

    // Create our peer document in the room
    const peerRef = doc(this.db, 'rooms', this.roomCode, 'peers', this.localPeerId);
    await setDoc(peerRef, {
      joinedAt: serverTimestamp(),
      isHost: true,
    });

    // Listen for new peers joining
    this.listenForPeers();

    // Listen for signaling messages to us
    this.listenForSignals();

    this.emit('roomCreated', { roomCode: this.roomCode });
    console.log(`[FirestoreSignaling] Room created: ${this.roomCode}`);

    return this.roomCode;
  }

  async joinRoom(roomCode: string): Promise<void> {
    if (!this.db) throw new Error('Not connected to Firebase');

    const { doc, getDoc, setDoc, updateDoc, serverTimestamp, arrayUnion } = this.firebase;

    this.roomCode = roomCode.toUpperCase();
    console.log(`[FirestoreSignaling] Attempting to join room: ${this.roomCode} as peer: ${this.localPeerId}`);

    // Check if room exists
    const roomRef = doc(this.db, 'rooms', this.roomCode);
    const roomSnap = await getDoc(roomRef);

    if (!roomSnap.exists()) {
      this.emit('error', { message: `Room ${this.roomCode} not found` });
      throw new Error(`Room ${this.roomCode} not found`);
    }

    const roomData = roomSnap.data();
    const existingPeers: string[] = roomData.peers || [];
    console.log(`[FirestoreSignaling] Room exists, current peers: [${existingPeers.join(', ')}]`);

    // Add ourselves to the room using arrayUnion for atomic update
    try {
      await updateDoc(roomRef, {
        peers: arrayUnion(this.localPeerId),
      });
      console.log(`[FirestoreSignaling] Added self to peers array`);
    } catch (e) {
      console.error(`[FirestoreSignaling] Failed to update peers array:`, e);
      // Fallback: try spread operator
      await updateDoc(roomRef, {
        peers: [...existingPeers, this.localPeerId],
      });
    }

    // Create our peer document
    const peerRef = doc(this.db, 'rooms', this.roomCode, 'peers', this.localPeerId);
    await setDoc(peerRef, {
      joinedAt: serverTimestamp(),
      isHost: false,
    });
    console.log(`[FirestoreSignaling] Created peer document`);

    // Listen for peers
    this.listenForPeers();

    // Listen for signaling messages to us
    this.listenForSignals();

    // Emit room joined with existing peers (excluding ourselves)
    const otherPeers = existingPeers.filter(p => p !== this.localPeerId);
    this.emit('roomJoined', { roomCode: this.roomCode, peers: otherPeers });

    console.log(`[FirestoreSignaling] Joined room: ${this.roomCode}, will connect to peers: [${otherPeers.join(', ')}]`);
  }

  private listenForPeers(): void {
    const { collection, onSnapshot } = this.firebase;

    const peersRef = collection(this.db, 'rooms', this.roomCode, 'peers');
    
    const unsubscribe = onSnapshot(peersRef, (snapshot: any) => {
      snapshot.docChanges().forEach((change: any) => {
        const peerId = change.doc.id;
        
        if (peerId === this.localPeerId) return; // Ignore ourselves

        if (change.type === 'added') {
          console.log(`[FirestoreSignaling] Peer joined: ${peerId}`);
          this.emit('peerJoined', { peerId });
        } else if (change.type === 'removed') {
          console.log(`[FirestoreSignaling] Peer left: ${peerId}`);
          this.emit('peerLeft', { peerId });
        }
      });
    });

    this.unsubscribers.push(unsubscribe);
  }

  private listenForSignals(): void {
    const { collection, onSnapshot, deleteDoc, doc } = this.firebase;

    // Listen for signals sent TO us
    const signalsRef = collection(this.db, 'rooms', this.roomCode, 'signals', this.localPeerId, 'messages');
    
    const unsubscribe = onSnapshot(signalsRef, (snapshot: any) => {
      snapshot.docChanges().forEach(async (change: any) => {
        if (change.type === 'added') {
          const data = change.doc.data();
          const messageId = change.doc.id;

          console.log(`[FirestoreSignaling] Received signal: ${data.type} from ${data.from}`);

          switch (data.type) {
            case 'offer':
              this.emit('offer', { 
                peerId: data.from, 
                offer: data.payload as RTCSessionDescriptionInit 
              });
              break;
            case 'answer':
              this.emit('answer', { 
                peerId: data.from, 
                answer: data.payload as RTCSessionDescriptionInit 
              });
              break;
            case 'ice-candidate':
              this.emit('iceCandidate', { 
                peerId: data.from, 
                candidate: data.payload as RTCIceCandidateInit 
              });
              break;
          }

          // Delete the message after processing (cleanup)
          const messageRef = doc(this.db, 'rooms', this.roomCode, 'signals', this.localPeerId, 'messages', messageId);
          await deleteDoc(messageRef);
        }
      });
    });

    this.unsubscribers.push(unsubscribe);
  }

  async sendOffer(targetId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    await this.sendSignal(targetId, 'offer', offer);
  }

  async sendAnswer(targetId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    await this.sendSignal(targetId, 'answer', answer);
  }

  async sendIceCandidate(targetId: string, candidate: RTCIceCandidateInit): Promise<void> {
    await this.sendSignal(targetId, 'ice-candidate', candidate);
  }

  private async sendSignal(targetId: string, type: string, payload: unknown): Promise<void> {
    if (!this.db) return;

    const { collection, addDoc, serverTimestamp } = this.firebase;

    // Add message to target's signal inbox
    const signalsRef = collection(this.db, 'rooms', this.roomCode, 'signals', targetId, 'messages');
    
    await addDoc(signalsRef, {
      type,
      from: this.localPeerId,
      payload,
      timestamp: serverTimestamp(),
    });

    console.log(`[FirestoreSignaling] Sent ${type} to ${targetId}`);
  }

  async leaveRoom(): Promise<void> {
    if (!this.db || !this.roomCode) return;

    const { doc, deleteDoc, getDoc, updateDoc } = this.firebase;

    // Unsubscribe from all listeners
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];

    // Remove our peer document
    const peerRef = doc(this.db, 'rooms', this.roomCode, 'peers', this.localPeerId);
    await deleteDoc(peerRef);

    // Remove ourselves from room's peer list
    const roomRef = doc(this.db, 'rooms', this.roomCode);
    const roomSnap = await getDoc(roomRef);
    
    if (roomSnap.exists()) {
      const roomData = roomSnap.data();
      const peers: string[] = roomData.peers || [];
      const newPeers = peers.filter(p => p !== this.localPeerId);
      
      if (newPeers.length === 0) {
        // Last person, delete the room
        await deleteDoc(roomRef);
        console.log(`[FirestoreSignaling] Room ${this.roomCode} deleted (empty)`);
      } else {
        await updateDoc(roomRef, { peers: newPeers });
      }
    }

    this.roomCode = '';
    console.log('[FirestoreSignaling] Left room');
  }

  disconnect(): void {
    this.leaveRoom();
    this.isConnected = false;
    this.emit('disconnected', undefined);
  }

  private generateRoomCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  getLocalPeerId(): string {
    return this.localPeerId;
  }

  getRoomCode(): string {
    return this.roomCode;
  }

  getIsConnected(): boolean {
    return this.isConnected;
  }
}
